import type { EnergyOverpaymentRuleSet } from "./types.js";

// Energy overpayment ruleset — DMO 8, 2026-27 (1 July 2026 – 30 June 2027).
//
// The AER's Default Market Offer (DMO) is the regulated electricity reference
// price for the three DMO jurisdictions (NSW / SE QLD / SA). It reprices ANNUALLY
// effective 1 July; the 2026-27 determination is the EIGHTH ("DMO 8"), final
// determination released 26 May 2026, effective 1 July 2026. This is the CURRENT
// determination as at the 2026-27 financial year.
//
// DMO 8 residential flat-rate reference prices (single-rate, no controlled load),
// effective 1 July 2026, transcribed from the AER DMO 2026-27 final determination
// (26 May 2026) and cross-checked against the per-zone restatement of the AER
// table (energyplans.com.au DMO 8 table) — figures agree to the dollar:
//
//   Zone                       DMO 8 (2026-27)   DMO 7 (2025-26)   Move
//   Ausgrid (NSW)              $1,899            $1,965            -3.4%
//   Endeavour (NSW)            $2,328            $2,411            -3.4%
//   Essential Energy (NSW)     $2,604            $2,741            -5.0%
//   Energex (SE QLD)           $1,988            $2,143            -7.2%
//   SA Power Networks (SA)     $2,334            $2,301            +1.4%
//
// Reconciliation against the DMO 7 figures (CITED in
// energy-overpayment-check-figures.md Fig 4) and the AER's published % moves
// (NSW res flat -3.4% to -5.0%, SEQ -7.2%, SA +1.4% — Fig 5):
//   $1,965 × (1 − 0.034) = $1,898 ≈ $1,899   (Ausgrid)
//   $2,411 × (1 − 0.034) = $2,329 ≈ $2,328   (Endeavour)
//   $2,741 × (1 − 0.050) = $2,604            (Essential)
//   $2,143 × (1 − 0.072) = $1,989 ≈ $1,988   (Energex)
//   $2,301 × (1 + 0.014) = $2,333 ≈ $2,334   (SA Power Networks)
// All five reconcile; the encoded values are the AER determination figures.
//
// NEVER edit this shipped file to "fix" a later DMO year — author a new dated
// ruleset (dmo9-2027-28.ts) and register it alongside this one. Historical "X%
// over the reference" verdicts must resolve against the determination in force on
// the assessed date.
//
// Loyalty-tax figures (ACCC, surfaced as a CITED RANGE, not a personalised
// saving): the two ends come from different ACCC report rounds — kept explicit.
//   Lower:  $238/yr  — offers >1 year old vs new (ACCC "No reward for being
//                       loyal", 30 Dec 2024).
//   Upper:  $221/yr  — same plan >3 years old vs new (ACCC Inquiry into the NEM
//                       report, Dec 2025 / media release 22 Dec 2025).
//   (energy-loyalty-tax-estimator-figures.md Figs 1 & 2, both CITED. The $221/3yr
//   figure is the LATEST ACCC headline; $238/1yr is the prior round — note both
//   are real ACCC figures from different cycles, so we present the RANGE.)

export const dmo8_2026_27: EnergyOverpaymentRuleSet = {
  determination: "DMO 8",
  period: "2026-27",
  effectiveFrom: "2026-07-01",
  effectiveTo: "2027-06-30",
  sources: {
    dmoDetermination:
      "https://www.aer.gov.au/industry/retail/default-market-offer",
    dmo8Release:
      "https://www.aer.gov.au/news/articles/news-releases/aer-releases-final-default-market-offer-2026-27",
    loyaltyTaxDec2024:
      "https://www.accc.gov.au/media-release/no-reward-for-being-loyal-australians-urged-to-shop-around-for-a-better-value-electricity-plan",
    loyaltyTaxDec2025:
      "https://www.accc.gov.au/media-release/accc-urges-households-to-change-electricity-plans-to-help-offset-price-increases",
    energyMadeEasy: "https://www.energymadeeasy.gov.au/",
  },
  sourcesVerifiedAt: "2026-06-23",
  zones: {
    AUSGRID: {
      annualReferencePrice: 1_899,
      label: "Ausgrid (Sydney, Central Coast, Hunter)",
    },
    ENDEAVOUR: {
      annualReferencePrice: 2_328,
      label: "Endeavour Energy (greater western Sydney, Blue Mountains, Illawarra)",
    },
    ESSENTIAL: {
      annualReferencePrice: 2_604,
      label: "Essential Energy (regional NSW)",
    },
    ENERGEX: {
      annualReferencePrice: 1_988,
      label: "Energex (Brisbane / South East Queensland)",
    },
    SA_POWER_NETWORKS: {
      annualReferencePrice: 2_334,
      label: "SA Power Networks (South Australia)",
    },
  },
  loyaltyTax: {
    lowerAnnual: 221,
    upperAnnual: 238,
    lowerBasis:
      "households on the same plan for more than three years pay on average $221/yr more than customers on new plans (ACCC, Dec 2025)",
    upperBasis:
      "customers on offers more than one year old pay on average $238/yr more than new-plan customers (ACCC, Dec 2024)",
  },
};
